import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Palmtree, LogOut, Loader2, ShieldAlert, Mail, Phone, Building2,
  Users, MapPin, CalendarClock, Inbox, CheckCircle2, XCircle, ArrowLeft, MessageSquare, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EspaceLeads() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | forbidden | ready
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!meRes.ok) throw new Error("unauth");
        const me = await meRes.json();
        setUser(me);
        if (!me.is_admin) { setStatus("forbidden"); return; }
        setStatus("ready");
        const res = await fetch(`${API}/propositions`, { credentials: "include" });
        if (res.ok) setLeads(await res.json());
      } catch {
        navigate("/login", { replace: true });
      } finally {
        setLoadingLeads(false);
      }
    })();
  }, [navigate]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login", { replace: true });
  };

  const exportCSV = () => {
    const headers = ["Entreprise", "Contact", "Email", "Téléphone", "Effectif", "Île souhaitée", "Période", "Commentaires", "Date", "Email envoyé"];
    const esc = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    const rows = leads.map((l) => [
      l.nom_entreprise, l.nom_complet, l.email_pro, l.telephone, l.effectif,
      l.ile_souhaitee, l.periode_souhaitee, l.commentaires,
      l.created_at ? new Date(l.created_at).toLocaleString("fr-FR") : "",
      l.email_sent ? "Oui" : "Non",
    ].map(esc).join(";"));
    const csv = "\uFEFF" + [headers.map(esc).join(";"), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-cse-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center text-white" data-testid="leads-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <div className="min-h-screen bg-ocean-dark flex items-center justify-center px-6 text-center" data-testid="leads-forbidden">
        <div className="max-w-md">
          <ShieldAlert className="w-14 h-14 text-coral mx-auto" strokeWidth={1.4} />
          <h1 className="mt-5 font-serif text-3xl font-semibold text-white">Accès réservé</h1>
          <p className="mt-3 text-sm text-white/60">
            Le compte <strong className="text-white">{user?.email}</strong> n'est pas autorisé à consulter cet espace.
            Contactez l'administrateur pour ajouter votre adresse.
          </p>
          <button onClick={logout} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-ocean-dark font-semibold px-5 py-3 text-sm" data-testid="forbidden-logout-button">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-light" data-testid="espace-leads-page">
      <header className="sticky top-0 z-40 bg-ocean-dark text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-serif text-lg font-semibold" data-testid="leads-logo">
            <Palmtree className="w-5 h-5 text-gold" strokeWidth={1.6} /> L'Échappée CSE
          </button>
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-gold/40" />
            ) : null}
            <span className="hidden sm:block text-sm text-white/70">{user?.name || user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-medium transition-colors" data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-muted2 hover:text-coral transition-colors mb-6" data-testid="leads-back-home">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </button>

        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-soft">Espace équipe CSE</span>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-ocean">Demandes de proposition</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCSV}
              disabled={leads.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-coral hover:bg-coral-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-5 py-3 text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(230,92,0,0.35)]"
              data-testid="leads-export-csv-button"
            >
              <Download className="w-4 h-4" /> Exporter CSV
            </button>
            <div className="flex items-center gap-2 rounded-2xl bg-white border border-ocean/5 px-5 py-3 shadow-sm">
              <Inbox className="w-5 h-5 text-palm" />
              <span className="font-serif text-2xl font-semibold text-ocean" data-testid="leads-count">{leads.length}</span>
              <span className="text-sm text-muted2">demande{leads.length > 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>

        {loadingLeads ? (
          <div className="mt-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-ocean" /></div>
        ) : leads.length === 0 ? (
          <div className="mt-16 text-center text-muted2" data-testid="leads-empty">
            <Inbox className="w-12 h-12 mx-auto text-ocean/20" />
            <p className="mt-4">Aucune demande pour le moment.</p>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid="leads-list">
            {leads.map((l, i) => (
              <motion.article
                key={l.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-3xl bg-white border border-ocean/5 p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`lead-card-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-ocean">{l.nom_entreprise}</h3>
                    <p className="text-sm text-muted2">{l.nom_complet}</p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2.5 py-1 ${l.email_sent ? "bg-palm-light text-palm" : "bg-orange-50 text-coral"}`}
                    title={l.email_sent ? "Email de notification envoyé" : "Email non envoyé"}
                  >
                    {l.email_sent ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    {l.email_sent ? "Notifié" : "Email KO"}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                  <Info icon={Mail} value={l.email_pro} href={`mailto:${l.email_pro}`} />
                  <Info icon={Phone} value={l.telephone} href={`tel:${l.telephone}`} />
                  <Info icon={Users} value={l.effectif} />
                  <Info icon={MapPin} value={l.ile_souhaitee} />
                  <Info icon={CalendarClock} value={l.periode_souhaitee} />
                  <Info icon={Building2} value={new Date(l.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })} />
                </div>

                {l.commentaires ? (
                  <div className="mt-4 flex gap-2 text-sm text-muted2 bg-sand-warm rounded-2xl p-3">
                    <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-gold-soft" />
                    <span>{l.commentaires}</span>
                  </div>
                ) : null}

                <a
                  href={`mailto:${l.email_pro}?subject=${encodeURIComponent("Votre Journée Bien-être CSE — Antilles 4*")}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-coral hover:bg-coral-hover text-white font-semibold px-5 py-2.5 text-sm transition-colors"
                  data-testid={`lead-reply-${i}`}
                >
                  <Mail className="w-4 h-4" /> Répondre au prospect
                </a>
              </motion.article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Info = ({ icon: Icon, value, href }) => {
  const content = (
    <span className="flex items-center gap-1.5 text-muted2 truncate">
      <Icon className="w-3.5 h-3.5 flex-shrink-0 text-ocean-lagoon" />
      <span className="truncate">{value}</span>
    </span>
  );
  return href ? <a href={href} className="hover:text-coral transition-colors truncate">{content}</a> : content;
};
